import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Icon, IconNames } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Container, Display, List, ListItem } from './index.styles';
import { useCalendarContext } from '../../context';
import { dailyStartDate } from '../../utils';

interface Option {
  action: string;
  label: string;
  secondaryLabel: string;
  icon: IconNames;
}

export const ModesDropdown = () => {
  const { currentDate, setCurrentDate, setViewMode, todayDate, userWeekStartDay, viewMode } = useCalendarContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('calendar');

  const displayLabel = viewMode === 'monthly' ? t('monthly') : viewMode === 'weekly' ? t('weekly') : t('daily');
  const displayIcon = viewMode === 'monthly' ? 'calendar' : viewMode === 'weekly' ? 'column' : 'align_justify';

  const { ref: inViewRef } = useInView({
    threshold: 1,
    rootMargin: '8px',
    onChange: (nextInView) => {
      if (!nextInView) {
        setIsCollapsed(false);
      }
    },
  });

  const { x, y, strategy, refs } = useFloating({
    open: isCollapsed,
    onOpenChange: setIsCollapsed,
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const { setReference, setFloating } = refs;

  const handleViewModeSelect = (mode: CalendarView) => {
    // The weekly and monthly views move the current date by a week or a month, so it is only some day inside what
    // they show. The daily view opens on the first day of that week or month instead (or today, if it holds today).
    if (mode === 'daily' && viewMode !== 'daily') setCurrentDate(dailyStartDate(viewMode, currentDate, todayDate, userWeekStartDay));

    setViewMode(mode);
    setIsCollapsed(false);
    window.pendo.track('[Publish] Calendar - View mode changed', { mode });
  };

  const calendarModes: Option[] = [
    { action: 'monthly', label: t('monthly'), secondaryLabel: t('calendar'), icon: 'calendar' },
    { action: 'weekly', label: t('weekly'), secondaryLabel: t('board'), icon: 'column' },
    { action: 'daily', label: t('daily'), secondaryLabel: t('list'), icon: 'align_justify' },
  ];

  useEffect(() => {
    if (!isCollapsed) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const path = (event.composedPath && event.composedPath()) || [];
      const inContainer = containerRef.current ? containerRef.current.contains(target) || path.includes(containerRef.current) : false;
      const inDropdown = dropdownRef.current ? dropdownRef.current.contains(target) || path.includes(dropdownRef.current) : false;

      if (!inContainer && !inDropdown) {
        setIsCollapsed(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsCollapsed(false);
    };

    document.addEventListener('keydown', handleKey);

    return () => document.removeEventListener('keydown', handleKey);
  }, [isCollapsed]);

  return (
    <Container
      data-testid="calendar-mode-dropdown"
      ref={containerRef}
      data-export-hide
    >
      <Display
        id="calendar-mode-button"
        onClick={() => setIsCollapsed((prev) => !prev)}
        onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && setIsCollapsed((prev) => !prev)}
        ref={setReference}
        tabIndex={0}
      >
        <Icon
          customClass="display-icon"
          color="brandDark"
          name={displayIcon}
          strokeWidth={2}
        />
        <span>{displayLabel}</span>
      </Display>

      {isCollapsed &&
        createPortal(
          <List
            ref={(node) => {
              setFloating(node);
              inViewRef(node);
              dropdownRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            {calendarModes.map((mode) => (
              <ListItem
                data-testid={`calendar-mode-option-${mode.action}`}
                key={mode.action}
                onClick={() => handleViewModeSelect(mode.action as CalendarView)}
                id={`calendar-mode-option-${mode.action}`}
              >
                <Icon
                  color="brandDark"
                  name={mode.icon}
                  strokeWidth={2}
                />
                <p>{mode.label}</p>
                <span>{mode.secondaryLabel}</span>
              </ListItem>
            ))}
          </List>,
          document.getElementById('dropdown-root') as HTMLElement
        )}
    </Container>
  );
};
